/* DerbyLys – product page logic. No dependencies. */
(() => {
  'use strict';

  const S = () => (window.PG && window.PG.strings) || {};

  /* ---------------- Gallery ---------------- */
  class Gallery {
    constructor(root) {
      this.root = root;
      this.slides = [...root.querySelectorAll('.gallery__slide')];
      this.thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
      this.counter = root.querySelector('[data-gallery-counter]');
      this.caption = root.querySelector('[data-gallery-caption]');
      this.stage = root.querySelector('[data-gallery-stage]');
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      if (this.slides.length < 2) return;

      root.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      root.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.galleryThumb))));
      this.stage.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
      });

      let x0 = null, y0 = null;
      this.stage.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
      this.stage.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        const dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.go(this.index + (dx < 0 ? 1 : -1));
        x0 = y0 = null;
      });
    }

    go(i) {
      const n = this.slides.length;
      if (!n) return;
      const next = ((i % n) + n) % n;
      this.slides.forEach((s, k) => {
        const on = k === next;
        s.classList.toggle('is-active', on);
        if (on) s.removeAttribute('aria-hidden'); else s.setAttribute('aria-hidden', 'true');
        if (!on) s.querySelectorAll('video').forEach((v) => v.pause());
      });
      this.thumbs.forEach((t, k) => {
        t.classList.toggle('is-active', k === next);
        if (k === next) {
          t.setAttribute('aria-current', 'true');
          const strip = t.closest('.gallery__thumbs');
          if (strip && strip.scrollWidth > strip.clientWidth) {
            const li = t.parentElement;
            strip.scrollTo({ left: li.offsetLeft - strip.clientWidth / 2 + li.clientWidth / 2, behavior: 'smooth' });
          }
        } else t.removeAttribute('aria-current');
      });
      const pad = (v) => String(v).padStart(2, '0');
      if (this.counter) this.counter.textContent = `${pad(next + 1)} / ${pad(n)}`;
      if (this.caption) {
        const cap = this.slides[next].dataset.caption || '';
        this.caption.textContent = cap;
        this.caption.hidden = !cap;
      }
      this.index = next;
    }

    goToMedia(id) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i >= 0 && i !== this.index) this.go(i);
    }
  }

  /* ---------------- Product form ---------------- */
  class PgProduct {
    constructor(root) {
      this.root = root;
      const json = root.querySelector('[data-pg-product-json]');
      if (!json) return;
      try { this.data = JSON.parse(json.textContent); } catch (e) { console.error('DerbyLys: bad product JSON', e); return; }

      this.form = root.querySelector('[data-product-form]');
      this.variantInput = root.querySelector('[data-variant-input]');
      this.cta = root.querySelector('[data-cta]');
      this.sticky = root.querySelector('[data-sticky-bar]');
      this.stickyCta = root.querySelector('[data-sticky-cta]');
      this.teamsBox = root.querySelector('[data-teams]');
      this.sameToggle = root.querySelector('[data-same-toggle]');
      this.doorProps = root.querySelector('[data-door-properties]');
      this.error = root.querySelector('[data-form-error]');
      this.sameTeam = this.data.sameTeamDefault !== false;
      this.teamMap = {};
      (this.data.teams || []).forEach((t) => { this.teamMap[t.name] = t; });

      const g = root.querySelector('[data-gallery]');
      this.gallery = g ? new Gallery(g) : null;

      this.bind();
      this.update({ initial: true });
      this.initSticky();
      this.broadcastTeams();
    }

    /* ---- helpers ---- */
    $$(sel) { return [...this.root.querySelectorAll(sel)]; }

    currentOptions() {
      const opts = new Array(this.data.optionCount).fill(null);
      this.$$('[data-option-index]').forEach((group) => {
        const i = Number(group.dataset.optionIndex);
        const checked = group.querySelector('input[type="radio"]:checked');
        const select = group.querySelector('select[data-team-primary]');
        if (checked) opts[i] = checked.value;
        else if (select) opts[i] = select.value;
      });
      // fill any option not rendered (e.g. single-value options) from the first variant
      const first = this.data.variants[0];
      return opts.map((o, i) => (o == null && first ? first.options[i] : o));
    }

    findVariant(opts) {
      return this.data.variants.find((v) => v.options.every((o, i) => o === opts[i])) || null;
    }

    variantForTier(value, opts) {
      const b = this.data.bundleIndex;
      const want = opts.slice(); want[b] = value;
      return this.findVariant(want) || this.data.variants.find((v) => v.options[b] === value) || null;
    }

    doorsFor(variant, opts) {
      if (variant) return variant.doors || 1;
      const b = this.data.bundleIndex;
      if (b < 0) return 1;
      const any = this.data.variants.find((v) => v.options[b] === opts[b]);
      return any ? any.doors : 1;
    }

    doorName(i) {
      const s = S();
      return (s.doorNames && s.doorNames[i]) || String(s.doorN || 'Door %N%').replace('%N%', i + 1);
    }

    /* ---- events ---- */
    bind() {
      this.$$('[data-option-input]').forEach((el) => el.addEventListener('change', () => {
        if (el.matches('[data-team-primary]') && this.sameTeam) this.syncExtras();
        this.update();
      }));
      this.$$('[data-team-extra]').forEach((el) => el.addEventListener('change', () => this.update({ teamsOnly: true })));

      this.sameToggle?.addEventListener('click', () => {
        this.sameTeam = !this.sameTeam;
        if (this.sameTeam) this.syncExtras();
        this.update({ teamsOnly: true });
      });

      this.$$('[data-qty-step]').forEach((b) => b.addEventListener('click', () => {
        const input = this.root.querySelector('.qty__input');
        if (!input) return;
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(b.dataset.qtyStep));
      }));

      this.form?.addEventListener('submit', (e) => this.onSubmit(e));

      // Door preview chips can select a team
      document.addEventListener('pg:select-team', (e) => {
        const select = this.root.querySelector('[data-team-primary]');
        if (!select || !e.detail || !e.detail.name) return;
        if ([...select.options].some((o) => o.value === e.detail.name)) {
          select.value = e.detail.name;
          if (this.sameTeam) this.syncExtras();
          this.update();
        }
      });
      document.addEventListener('pg:request-teams', () => this.broadcastTeams());
    }

    syncExtras() {
      const primary = this.root.querySelector('[data-team-primary]');
      if (!primary) return;
      this.$$('[data-team-extra]').forEach((s) => { s.value = primary.value; });
    }

    /* ---- render ---- */
    update({ initial = false, teamsOnly = false } = {}) {
      const s = S();
      const opts = this.currentOptions();
      const v = this.findVariant(opts);
      this.variant = v;

      if (!teamsOnly) {
        // hidden variant id
        if (this.variantInput) {
          this.variantInput.value = v ? v.id : '';
          this.variantInput.disabled = !v;
        }

        // prices
        const set = (sel, text, hide) => this.$$(sel).forEach((el) => { if (text != null) el.textContent = text; el.hidden = !!hide; });
        set('[data-price]', v ? v.price : '');
        set('[data-compare]', v && v.compare ? v.compare : '', !(v && v.compare));
        set('[data-save]', v && v.save ? v.save : '', !(v && v.save));
        set('[data-sticky-price]', v ? v.price : '');
        set('[data-sticky-compare]', v && v.compare ? v.compare : '', !(v && v.compare));

        // stock badge
        const label = this.root.querySelector('[data-stock-label]');
        const dot = this.root.querySelector('[data-stock-badge] .dot');
        if (label) label.textContent = v && v.available ? label.dataset.inStock : s.soldOut;
        if (dot) { dot.classList.toggle('dot--live', !!(v && v.available)); dot.classList.toggle('dot--off', !(v && v.available)); }

        // tiers
        if (this.data.bundleIndex >= 0) {
          this.$$('[data-tier]').forEach((tierEl) => {
            const input = tierEl.querySelector('input');
            tierEl.classList.toggle('is-selected', input.checked);
            const card = tierEl.closest('[data-tier-card]');
            if (card) {
              card.classList.toggle('is-selected', input.checked);
              const slot = card.querySelector('[data-tier-slot]');
              if (input.checked && slot && this.teamsBox && this.teamsBox.parentElement !== slot) slot.appendChild(this.teamsBox);
            }
            const tv = this.variantForTier(input.value, opts);
            if (!tv) return;
            const sub = tierEl.querySelector('[data-tier-sub]');
            if (sub) sub.textContent = tv.sub || sub.dataset.default || '';
            const p = tierEl.querySelector('[data-tier-price]');
            const c = tierEl.querySelector('[data-tier-compare]');
            const pct = tierEl.querySelector('[data-tier-pct]');
            const unit = tierEl.querySelector('[data-tier-unit]');
            if (p) p.textContent = tv.price;
            if (c) { c.textContent = tv.compare || ''; c.hidden = !tv.compare; }
            if (pct) { pct.textContent = tv.pct || ''; pct.hidden = !tv.pct; }
            if (unit && tv.unit && unit.dataset.tpl) {
              unit.textContent = (unit.dataset.prefix || '') + unit.dataset.tpl.replace('%PRICE%', tv.unit);
            }
          });
        }

        // CTA
        this.setCta('idle');

        // media
        if (v && v.media && this.gallery && !initial) this.gallery.goToMedia(v.media);

        // URL
        if (v && !initial && /\/products\//.test(window.location.pathname)) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      this.renderTeams(opts, v);
    }

    renderTeams(opts, v) {
      if (!this.teamsBox) { this.updateStickyTitle(opts, [opts[this.data.teamIndex]]); return; }
      const doors = Math.max(1, this.doorsFor(v, opts));
      const rows = this.$$('[data-door-row]');
      const multi = doors > 1 && !this.sameTeam;

      if (this.sameToggle) {
        this.sameToggle.hidden = doors < 2;
        this.sameToggle.setAttribute('aria-checked', String(this.sameTeam));
      }

      rows.forEach((row, i) => {
        const show = multi ? i < doors : i === 0;
        row.hidden = !show;
        const lab = row.querySelector('[data-door-label]');
        if (lab) lab.textContent = multi ? this.doorName(i) : (doors > 1 ? S().allDoors : this.doorName(0));
        const sel = row.querySelector('select');
        const t = this.teamMap[sel.value];
        const sw = row.querySelector('[data-team-swatch]');
        if (sw && t) {
          if (t.img) { sw.style.backgroundImage = `url("${t.img}")`; sw.classList.add('has-img'); }
          else { sw.style.backgroundImage = ''; sw.classList.remove('has-img'); sw.style.background = t.c1; sw.style.boxShadow = `inset 0 0 0 3px ${t.c2}`; }
        }
      });

      // which team per door
      const teams = rows.slice(0, doors).map((row, i) => (multi ? row.querySelector('select').value : rows[0].querySelector('select').value));

      // line item properties for mixed teams
      if (this.doorProps) {
        this.doorProps.innerHTML = '';
        const mixed = multi && new Set(teams).size > 1;
        if (mixed) {
          teams.forEach((team, i) => {
            const inp = document.createElement('input');
            inp.type = 'hidden';
            inp.name = `properties[${this.doorName(i)}]`;
            inp.value = team;
            this.doorProps.appendChild(inp);
          });
        }
      }

      // preview link
      const link = this.root.querySelector('[data-preview-link-text]');
      if (link && link.dataset.tpl) link.textContent = link.dataset.tpl.replace('%TEAM%', teams[0]);

      this.updateStickyTitle(opts, teams);
      document.dispatchEvent(new CustomEvent('pg:team-change', { detail: { team: this.teamMap[teams[0]] || { name: teams[0] } } }));
    }

    updateStickyTitle(opts, teams) {
      const el = this.root.querySelector('[data-sticky-title]');
      if (!el) return;
      const parts = [this.data.productTitle];
      if (this.data.bundleIndex >= 0) parts.push(opts[this.data.bundleIndex]);
      const uniq = [...new Set((teams || []).filter(Boolean))];
      if (uniq.length) parts.push(uniq.join(', '));
      el.textContent = parts.join(' · ');
    }

    broadcastTeams() {
      const primary = this.root.querySelector('[data-team-primary]');
      document.dispatchEvent(new CustomEvent('pg:teams', {
        detail: { teams: this.data.teams || [], selected: primary ? primary.value : null },
      }));
      if (primary) document.dispatchEvent(new CustomEvent('pg:team-change', { detail: { team: this.teamMap[primary.value] || { name: primary.value } } }));
    }

    setCta(state) {
      const s = S();
      const v = this.variant;
      [this.cta, this.stickyCta].forEach((btn) => {
        if (!btn) return;
        const isSticky = btn === this.stickyCta;
        const label = btn.querySelector('[data-cta-label]');
        const icon = btn.querySelector('[data-cta-icon]');
        const done = btn.querySelector('.cta__icon--done');
        btn.classList.toggle('is-loading', state === 'loading');
        btn.classList.toggle('is-added', state === 'added');
        if (icon) icon.hidden = state === 'added';
        if (done) done.hidden = state !== 'added';
        btn.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');

        let text;
        if (!v) { text = s.unavailable; btn.disabled = true; }
        else if (!v.available) { text = s.soldOut; btn.disabled = true; }
        else {
          btn.disabled = state === 'loading';
          if (state === 'loading') text = s.adding;
          else if (state === 'added') text = isSticky ? s.addedShort : s.added;
          else text = isSticky ? s.stickyCta : `${s.addToCart} · ${v.price}`;
        }
        if (label) label.textContent = text;
      });
    }

    /* ---- add to cart ---- */
    async onSubmit(e) {
      e.preventDefault();
      if (!this.variant || !this.variant.available) return;
      if (this.error) this.error.hidden = true;
      this.setCta('loading');

      const fd = new FormData(this.form);
      [...fd.keys()].filter((k) => k.startsWith('option-')).forEach((k) => fd.delete(k));
      const useDrawer = window.PG && window.PG.cartType === 'drawer' && window.PG.cart;
      if (useDrawer) {
        fd.append('sections', window.PG.cart.sectionIds().join(','));
        fd.append('sections_url', window.location.pathname);
      }

      try {
        const res = await fetch(`${window.PG.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: fd,
        });
        const json = await res.json();
        if (!res.ok || json.status) {
          throw new Error(json.description || json.message || S().cartError);
        }
        this.setCta('added');
        clearTimeout(this.ctaTimer);
        this.ctaTimer = setTimeout(() => this.setCta('idle'), 1800);

        if (useDrawer) {
          window.PG.cart.renderSections(json.sections);
          window.PG.cart.open(this.cta);
        } else {
          window.location.href = window.PG.routes.cart;
        }
      } catch (err) {
        this.setCta('idle');
        if (this.error) {
          this.error.textContent = err.message || S().cartError;
          this.error.hidden = false;
        }
      }
    }

    /* ---- sticky bar ---- */
    initSticky() {
      if (!this.sticky || !this.cta || !('IntersectionObserver' in window)) return;
      const show = (on) => {
        this.sticky.classList.toggle('is-visible', on);
        this.sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (this.stickyCta) this.stickyCta.tabIndex = on ? 0 : -1;
      };
      // Visible once the main button has scrolled above the viewport. Checked on scroll as well,
      // because an anchor jump can skip past the button without an intersection change.
      const check = () => {
        const r = this.cta.getBoundingClientRect();
        show(r.bottom < 0);
      };
      const io = new IntersectionObserver(check, { threshold: 0 });
      io.observe(this.cta);
      let raf = 0;
      window.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(check); }, { passive: true });
      window.addEventListener('resize', check, { passive: true });
      check();
    }
  }

  const init = () => document.querySelectorAll('[data-pg-product]').forEach((el) => {
    if (!el.__pg) el.__pg = new PgProduct(el);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
